import React, { useEffect, useState } from 'react';
import { BubbleButton, BubbleFormGroup, BubbleInput, BubbleTypeahead } from '../BubbleUI';
import { Brand, BrandVenueType } from '@go-shared/types/brand';
import { updateBrand } from '@go-client/lib/api/brand';
import { createPortal } from 'react-dom';
import { ObjectId } from 'bson';
import { BrandMealPeriod } from '@go-shared/lib/mealPeriods';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { mcmEndpointToIcon } from '@go-client/lib/mcm';
import { evaluateMcmEndpoint, toggleMcmEndpoint, mcmEndpointToName } from '@go-shared/lib/mcm';
import { useServerInfo } from '@go-client/context/ServerInfoContext';

interface BrandVenueLibraryFormDataBase {
  venueTypes: BrandVenueType[];
    mealPeriods: BrandMealPeriod[];
}

type BrandVenueLibraryFormData = BrandVenueLibraryFormDataBase & {
  _dirty: boolean;
  _errors: Partial<BrandEventLibraryFormError>;
};

type BrandEventLibraryFormError = { [K in keyof BrandVenueLibraryFormDataBase]: string };

function DEFAULT_FORM_DATA(brand?: Brand): BrandVenueLibraryFormData {
  return {
    venueTypes: brand?.venueTypes ?? [],
    mealPeriods: brand?.mealPeriods ?? [],

    _dirty: false,
    _errors: {},
  };
}

interface BrandFormProps {
  edit: Brand;
  onClose?: () => void;
  actionsElem?: HTMLDivElement;
  onFormSubmitted: () => Promise<void>;
}

export default function BrandEventLibraryForm(props: BrandFormProps) {
  const serverInfo = useServerInfo();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<BrandVenueLibraryFormData>(DEFAULT_FORM_DATA(props.edit));

  const venueTypeOpts = formData.venueTypes.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const mealPeriods = formData.mealPeriods.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  useEffect(() => {
    resetForm();
  }, [props.edit]);

  function resetForm() {
    setFormData(DEFAULT_FORM_DATA(props.edit));
  }

  function changeForm<T extends keyof BrandVenueLibraryFormDataBase>(
    key: T,
    val: BrandVenueLibraryFormDataBase[T]
  ) {
    setFormData({ ...formData, [key]: val, _dirty: true });
  }

  async function submitForm() {
    setSaving(true);
    try {
      if (props.edit) {
        // user intends to edit an existing property
        await updateBrand(props.edit._id, {
          ...props.edit,

          venueTypes: formData.venueTypes,
          mealPeriods: formData.mealPeriods,
        });
      }

      await props.onFormSubmitted();
      if (props.onClose) props.onClose();
    } finally {
      setSaving(false);
    }
  }

  const errored = Object.keys(formData._errors).length > 0;

  const formHtml = (
    <div className="grid grid-cols-3 gap-4">
      
            <BubbleFormGroup label="Venue Types" className="col-span-3">
              <BubbleTypeahead
                block
                options={venueTypeOpts}
                placeholder="Enter venue type name..."
                menuClassName="max-h-80 overflow-y-auto"
                allowNew
                noSuggestionsClick
                alwaysShowSuggestions={venueTypeOpts.length > 0}
                suggestionsText="No similar venue types found"
                suggestionClassName="p-1-i"
                suggestionsRenderFn={(_, opt) => (
                  <div className="flex items-center">
                    <BubbleInput
                      type="text"
                      block
                      appearance="bare"
                      value={opt.item.name}
                      className="flex-grow mr-3"
                      inputClassName="px-3 py-1 rounded"
                      onValue={val => {
                        const vtIdx = formData.venueTypes.findIndex(s => s._id === opt.item._id);
                        if (vtIdx < 0) return;
                        const newVTypes = [...formData.venueTypes];
                        newVTypes.splice(vtIdx, 1, { ...opt.item, name: val });
                        changeForm('venueTypes', newVTypes);
                      }}
                    />
      
                    {serverInfo.digitalOnlyMcmEdps.map(edp => {
                      const mcmVisible = evaluateMcmEndpoint('visible', edp, opt.item.mcm, undefined);
      
                      return (
                        <BubbleButton
                          key={edp}
                          className="p-1-i mr-1"
                          variant={mcmVisible ? 'navy' : 'smoke'}
                          inverse
                          icon={mcmEndpointToIcon(edp, mcmVisible)}
                          onClick={() => {
                            const vtIdx = formData.venueTypes.findIndex(s => s._id === opt.item._id);
                            if (vtIdx < 0) return;
                            const newVTypes = [...formData.venueTypes];
      
                            newVTypes.splice(vtIdx, 1, {
                              ...opt.item,
                              mcm: toggleMcmEndpoint(
                                'visible',
                                edp,
                                serverInfo.mcmEndpoints,
                                opt.item.mcm,
                                undefined
                              ),
                            });
                            changeForm('venueTypes', newVTypes);
                          }}
                        />
                      );
                    })}
                  </div>
                )}
                onCreate={val =>
                  changeForm('venueTypes', [
                    ...formData.venueTypes,
                    { _id: new ObjectId().toHexString(), name: val },
                  ])
                }
                onTrash={val =>
                  changeForm(
                    'venueTypes',
                    formData.venueTypes.filter(ar => ar._id !== val.item._id)
                  )
                }
              />
              <div className="italic text-slate mt-1 flex">
                <span className="mr-3">Legend:</span>
                {serverInfo.digitalOnlyMcmEdps.map(edp => (
                  <div key={edp} className="mr-3">
                    <FontAwesomeIcon fixedWidth icon={mcmEndpointToIcon(edp, true)} />
                    <span className="ml-1">{mcmEndpointToName(edp)}</span>
                  </div>
                ))}
              </div>
            </BubbleFormGroup>
            <BubbleFormGroup label="Meal Periods" className="col-span-4">
              <BubbleTypeahead
                block
                options={mealPeriods}
                placeholder="Enter meal period name..."
                menuClassName="max-h-80 overflow-y-auto"
                allowNew
                noSuggestionsClick
                alwaysShowSuggestions={mealPeriods.length > 0}
                suggestionsText="No similar meal periods found"
                suggestionClassName="p-1-i"
                suggestionsRenderFn={(_, opt) => (
                  <div className="flex items-center">
                    <BubbleInput
                      type="text"
                      block
                      placeholder="Name"
                      appearance="bare"
                      value={opt.item.name}
                      className="flex-grow mr-3"
                      inputClassName="px-3 py-1 rounded"
                      onValue={val => {
                        const arIdx = formData.mealPeriods.findIndex(s => s._id === opt.item._id);
                        if (arIdx < 0) return;
                        const newMealPeriods = [...formData.mealPeriods];
                        newMealPeriods.splice(arIdx, 1, { ...opt.item, name: val });
                        changeForm('mealPeriods', newMealPeriods);
                      }}
                    />
                    <BubbleInput
                      type="text"
                      placeholder="Code"
                      block
                      appearance="bare"
                      value={opt.item.code}
                      className="flex-grow mr-3"
                      inputClassName="px-3 py-1 rounded"
                      onValue={val => {
                        const arIdx = formData.mealPeriods.findIndex(s => s._id === opt.item._id);
                        if (arIdx < 0) return;
                        const newMealPeriods = [...formData.mealPeriods];
                        newMealPeriods.splice(arIdx, 1, { ...opt.item, code: val });
                        changeForm('mealPeriods', newMealPeriods);
                      }}
                    />
                    <span className="text-xs font-semibold uppercase text-navy mr-3">Color</span>
                    <BubbleInput
                      type="color"
                      value={opt.item.color}
                      className="mr-2"
                      onValue={val => {
                        const arIdx = formData.mealPeriods.findIndex(s => s._id === opt.item._id);
                        if (arIdx < 0) return;
                        const newMealPeriods = [...formData.mealPeriods];
                        newMealPeriods.splice(arIdx, 1, {
                          ...opt.item,
                          color: val,
                        });
                        changeForm('mealPeriods', newMealPeriods);
                      }}
                    />
                    <div className="flex items-center">
                      <div className="relative mr-3">
                        <BubbleInput
                          type="checkbox"
                          className="mr-1"
                          inputClassName="stretched-link"
                          checked={opt.item.type === 'sea'}
                          onValue={chk => {
                            if (chk) {
                              const arIdx = formData.mealPeriods.findIndex(s => s._id === opt.item._id);
                              if (arIdx < 0) return;
                              const newMealPeriods = [...formData.mealPeriods];
                              newMealPeriods.splice(arIdx, 1, {
                                ...opt.item,
                                type: 'sea',
                              });
                              changeForm('mealPeriods', newMealPeriods);
                            }
                          }}
                        />
                        <span>Ship</span>
                      </div>
                      <div className="relative">
                        <BubbleInput
                          type="checkbox"
                          className="mr-1"
                          inputClassName="stretched-link"
                          checked={opt.item.type === 'shore'}
                          onValue={chk => {
                            if (chk) {
                              const arIdx = formData.mealPeriods.findIndex(s => s._id === opt.item._id);
                              if (arIdx < 0) return;
                              const newMealPeriods = [...formData.mealPeriods];
                              newMealPeriods.splice(arIdx, 1, {
                                ...opt.item,
                                type: 'shore',
                              });
                              changeForm('mealPeriods', newMealPeriods);
                            }
                          }}
                        />
                        <span>Land</span>
                      </div>
                    </div>
                  </div>
                )}
                onCreate={val =>
                  changeForm('mealPeriods', [
                    ...formData.mealPeriods,
                    {
                      _id: new ObjectId().toHexString(),
                      code: '',
                      type: 'sea',
                      name: val,
                      color: '#000000',
                    },
                  ])
                }
                onTrash={val =>
                  changeForm(
                    'mealPeriods',
                    formData.mealPeriods.filter(ar => ar._id !== val.item._id)
                  )
                }
              />
            </BubbleFormGroup>
    </div>
  );

  const formActions = (
    <div className="flex items-center">
      <div className="ml-auto" />
      <BubbleButton
        variant="navy"
        onClick={resetForm}
        inverse
        disabled={!formData._dirty || errored || saving}
        icon="rotate-left"
      >
        Reset
      </BubbleButton>
      <BubbleButton
        variant="navy"
        onClick={submitForm}
        disabled={!formData._dirty || errored || saving}
        icon="floppy-disk"
      >
        Save Changes
      </BubbleButton>
    </div>
  );

  const portalButtons = props.actionsElem ? createPortal(formActions, props.actionsElem) : <></>;

  return (
    <>
      {formHtml}
      {portalButtons}
    </>
  );
}
